// Presentation helpers. Tenant API timestamps are UNIX SECONDS (ARCHITECTURE §0).

export function formatDate(unixSeconds: number | null | undefined): string {
  if (!unixSeconds) return "—";
  const d = new Date(unixSeconds * 1000);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatDateShort(unixSeconds: number | null | undefined): string {
  if (!unixSeconds) return "—";
  const d = new Date(unixSeconds * 1000);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/** Relative "time ago" for activity feeds; falls back to a short date. */
export function timeAgo(unixSeconds: number | null | undefined): string {
  if (!unixSeconds) return "—";
  const diff = Date.now() / 1000 - unixSeconds;
  if (diff < 0) return formatDateShort(unixSeconds);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return formatDateShort(unixSeconds);
}

/** Urgency tier for an SLA / due countdown — drives the chip colour class. */
export type SlaTone = "ok" | "warn" | "over" | "met" | "none";

export interface SlaCountdown {
  tone: SlaTone;
  /** Humanized label, e.g. "Due in 2h 10m", "Overdue 35m", "Met", "No SLA". */
  label: string;
  /** Longer text for the chip's aria-label / title. */
  aria: string;
}

/** Compact "2h 10m" / "45m" / "3d 4h" from a positive second span (for chips). */
function humanizeSpan(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (d) return h ? `${d}d ${h}h` : `${d}d`;
  if (h) return m ? `${h}h ${m}m` : `${h}h`;
  if (m) return `${m}m`;
  return "<1m";
}

/**
 * Compute an SLA countdown chip from a due timestamp (unix seconds).
 *
 * Colour tiers:
 *   - green  ("ok")   — plenty of time (> 25% of the window remaining)
 *   - amber  ("warn") — due soon (≤ 25% of the window, or < 1h when no window)
 *   - red    ("over") — overdue / breached
 *   - "met"           — the target was satisfied (satisfiedAt set)
 *   - "none"          — no due time available
 *
 * `windowSeconds` (created→due span) lets us scale the amber threshold to the
 * policy length; when unknown we fall back to a flat 1-hour "soon" cutoff.
 * `nowSeconds` is injectable so the live ticker re-computes on its own interval.
 */
export function slaCountdown(
  dueSeconds: number | null | undefined,
  opts: {
    nowSeconds?: number;
    satisfiedAt?: number | null;
    windowSeconds?: number | null;
    /** What the due time represents, for the aria/title text. */
    kindLabel?: string;
  } = {},
): SlaCountdown {
  const kind = opts.kindLabel ?? "SLA";
  if (opts.satisfiedAt) {
    return { tone: "met", label: "Met", aria: `${kind} met` };
  }
  if (!dueSeconds) {
    return { tone: "none", label: "No SLA", aria: `No ${kind} due time` };
  }
  const now = opts.nowSeconds ?? Date.now() / 1000;
  const remaining = dueSeconds - now;

  if (remaining <= 0) {
    const span = humanizeSpan(-remaining);
    return { tone: "over", label: `Overdue ${span}`, aria: `${kind} overdue by ${span}` };
  }

  const span = humanizeSpan(remaining);
  // "Soon" threshold: 25% of the policy window if we know it, else a flat hour.
  const soonCutoff =
    opts.windowSeconds && opts.windowSeconds > 0 ? opts.windowSeconds * 0.25 : 3600;
  const tone: SlaTone = remaining <= soonCutoff ? "warn" : "ok";
  return { tone, label: `Due in ${span}`, aria: `${kind} due in ${span}` };
}

export function formatDuration(totalSeconds: number | null | undefined): string {
  if (!totalSeconds || totalSeconds < 0) return "0m";
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

/** Friendly estimate from minutes: "2h 30m", "3h", "150m" (or "—" when unset). */
export function formatEstimate(minutes: number | null | undefined): string {
  if (minutes == null || minutes < 0) return "—";
  if (minutes === 0) return "0m";
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** Per-tenant ticket-number display config (prefix + zero-padding width). The
 *  raw sequential int is what's stored; this is presentation only. */
export interface TicketNumberFormat {
  prefix: string;
  padding: number;
}

/** Default = legacy bare integer (no prefix, no padding). */
export const DEFAULT_TICKET_NUMBER_FORMAT: TicketNumberFormat = { prefix: "", padding: 0 };

/**
 * Format a raw ticket number for display: `prefix` + the number zero-padded to
 * at least `padding` digits. E.g. ({prefix:"TKT-",padding:5}, 42) → "TKT-00042".
 * With the default config (no prefix, padding 0) this is just `String(n)`.
 */
export function formatTicketNumber(
  n: number | null | undefined,
  cfg: TicketNumberFormat = DEFAULT_TICKET_NUMBER_FORMAT,
): string {
  if (n == null) return "";
  const digits = Math.max(0, Math.floor(cfg.padding || 0));
  const body = String(Math.trunc(n)).padStart(digits, "0");
  return `${cfg.prefix ?? ""}${body}`;
}

/**
 * Format a raw facility work-order number for display. Identical semantics to
 * {@link formatTicketNumber} (prefix + zero-padding); kept as a named function
 * so work-order surfaces read intent clearly and can diverge later if needed.
 * E.g. ({prefix:"FAC-",padding:5}, 42) → "FAC-00042".
 */
export function formatFacilityNumber(
  n: number | null | undefined,
  cfg: TicketNumberFormat = DEFAULT_TICKET_NUMBER_FORMAT,
): string {
  return formatTicketNumber(n, cfg);
}

/**
 * Format a raw change-request number for display. Identical semantics to
 * {@link formatTicketNumber} (prefix + zero-padding); kept as a named function
 * so change-management surfaces read intent clearly and can diverge later if
 * needed. E.g. ({prefix:"CHG-",padding:4}, 42) → "CHG-0042".
 */
export function formatChangeNumber(
  n: number | null | undefined,
  cfg: TicketNumberFormat = DEFAULT_TICKET_NUMBER_FORMAT,
): string {
  return formatTicketNumber(n, cfg);
}

export function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes < 0) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function parseJson<T = unknown>(raw: string | null | undefined): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

export function titleCase(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** The product brand, shown when the host doesn't encode a tenant. */
export const BRAND = "ActionConnect";

/** Resolve the tenant org name from the host ({org}.actionconnect.com). On hosts
 *  that don't encode a tenant — the shared dev domains (e.g. the staging app at
 *  ac-app-web-staging.pages.dev / *.workers.dev) and local — there's no org label
 *  to read, so show the brand instead of a meaningless host segment. */
export function orgFromHost(): string {
  try {
    const host = window.location.hostname;
    if (
      !host ||
      host === "localhost" ||
      /^\d+(\.\d+)*$/.test(host) ||
      host.endsWith(".pages.dev") ||
      host.endsWith(".workers.dev")
    ) {
      return BRAND;
    }
    const label = host.split(".")[0] ?? "";
    if (!label) return BRAND;
    return titleCase(label);
  } catch {
    return BRAND;
  }
}
